import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  FileButton,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconFileTypePdf, IconPhoto, IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { shraniEnega } from './dokumenti';
import { pomanjsajVse } from '../api/slike';
import { NapakaApi, sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { casIzMs } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';

type Dokument = Shema['DokumentVozila'];

function velikost(b: number | undefined): string {
  const n = b ?? 0;
  return n < 1024 * 1024
    ? `${Math.max(1, Math.round(n / 1024))} KB`
    : `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Dokumenti vozila (novo v spletu): prometno dovoljenje, COC, pogodba … – slike in PDF, ki niso javni. Ogled v novem
 * zavihku, dodajanje in izbris; ob dodajanju vozila se sem shranijo dokumenti iz branja z umetno inteligenco.
 */
export function DokumentiVozila({ voziloId }: { voziloId: number }) {
  const odjemalec = useQueryClient();
  const dokumenti = useQuery({
    queryKey: ['dokumenti-vozila', voziloId],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/vozila/{id}/dokumenti', { signal, params: { path: { id: voziloId } } })),
  });
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dodajam, setDodajam] = useState(false);
  const [izbris, setIzbris] = useState<Dokument | null>(null);
  const [brisem, setBrisem] = useState(false);

  const dodaj = async (datoteke: File[]) => {
    setNapaka(null);
    setDodajam(true);
    try {
      for (const f of await pomanjsajVse(datoteke)) {
        await shraniEnega(voziloId, f);
      }
    } catch (e) {
      setNapaka(e instanceof NapakaApi && e.polja.datoteka ? e.polja.datoteka : sporocilo(e));
    } finally {
      setDodajam(false);
      await odjemalec.invalidateQueries({ queryKey: ['dokumenti-vozila', voziloId] });
    }
  };

  const odpri = (d: Dokument) => {
    setNapaka(null);
    odpriPdf(
      api.GET('/api/vozila/{id}/dokumenti/{dokumentId}', {
        params: { path: { id: voziloId, dokumentId: d.id! } },
        parseAs: 'blob',
      }),
    ).catch((e: unknown) => setNapaka(sporocilo(e)));
  };

  const izbrisi = async () => {
    setBrisem(true);
    try {
      await izvedi(
        api.DELETE('/api/vozila/{id}/dokumenti/{dokumentId}', {
          params: { path: { id: voziloId, dokumentId: izbris!.id! } },
        }),
      );
      setIzbris(null);
      await odjemalec.invalidateQueries({ queryKey: ['dokumenti-vozila', voziloId] });
    } catch (e) {
      setNapaka(sporocilo(e));
      setIzbris(null);
    } finally {
      setBrisem(false);
    }
  };

  return (
    <Kartica
      naslov="Dokumenti"
      dejanja={
        <FileButton onChange={(f) => void dodaj(f)} accept="image/*,application/pdf" multiple>
          {(p) => (
            <Button {...p} size="xs" variant="light" leftSection={<IconPlus size={14} />} loading={dodajam}>
              Dodaj
            </Button>
          )}
        </FileButton>
      }
    >
      <Stack gap="xs">
        {napaka && (
          <Alert color="red" variant="light" role="alert">
            {napaka}
          </Alert>
        )}
        {!dokumenti.data ? (
          <StanjeNalaganja
            napaka={dokumenti.error}
            ponovi={() => void dokumenti.refetch()}
            naslov="Dokumentov ni bilo mogoče naložiti"
          />
        ) : dokumenti.data.length === 0 ? (
          <Text size="sm" c="dimmed">
            Ni dokumentov. Dodajte prometno dovoljenje, pogodbo … (slike ali PDF, vidni samo vam).
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={360}>
            <Table verticalSpacing={6}>
              <Table.Tbody>
                {dokumenti.data.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {d.vrsta === 'application/pdf' ? <IconFileTypePdf size={18} /> : <IconPhoto size={18} />}
                        <Anchor component="button" size="sm" onClick={() => odpri(d)} style={{ textAlign: 'left' }}>
                          {d.ime}
                        </Anchor>
                      </Group>
                    </Table.Td>
                    <Table.Td className="brez-preloma">
                      <Text size="xs" c="dimmed">
                        {velikost(d.velikost)} · {casIzMs(d.ustvarjen)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Tooltip label="Izbriši">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setIzbris(d)}
                          aria-label={`Izbriši ${d.ime}`}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
      <Modal opened={izbris !== null} onClose={() => setIzbris(null)} title="Izbriši dokument" radius="lg">
        <Stack gap="sm">
          <Text size="sm">Dokument {izbris?.ime} bo izbrisan. Tega ni mogoče razveljaviti.</Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setIzbris(null)}>
              Prekliči
            </Button>
            <Button color="red" loading={brisem} onClick={() => void izbrisi()}>
              Izbriši
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Kartica>
  );
}
